import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Activity, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Wifi, WifiOff, Clock, Cpu, Volume2, Music,
  Film, Image as ImageIcon, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_META = {
  openai: { label: 'OpenAI', capability: 'Text + Image', icon: FileText, color: 'from-emerald-500/20 to-teal-500/20', accent: 'emerald' },
  elevenlabs: { label: 'ElevenLabs', capability: 'Voice', icon: Volume2, color: 'from-blue-500/20 to-indigo-500/20', accent: 'blue' },
  beatoven: { label: 'Beatoven', capability: 'Music', icon: Music, color: 'from-purple-500/20 to-violet-500/20', accent: 'purple' },
  sora: { label: 'Sora', capability: 'Video', icon: Film, color: 'from-orange-500/20 to-amber-500/20', accent: 'orange' },
  nanobanana: { label: 'Nano Banana', capability: 'Image (Gemini)', icon: ImageIcon, color: 'from-pink-500/20 to-rose-500/20', accent: 'pink' },
};

const STATUS_DISPLAY = {
  connected: { label: 'Connected', icon: CheckCircle2, className: 'text-green-400 bg-green-500/10 border-green-500/20' },
  error: { label: 'Unavailable', icon: XCircle, className: 'text-red-400 bg-red-500/10 border-red-500/20' },
  unknown: { label: 'Unknown', icon: AlertTriangle, className: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
};

export default function DiagnosticsPage() {
  const { token } = useAuth();
  const [providers, setProviders] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiagnostics = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/diagnostics/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(resp.data.providers);
      setCheckedAt(resp.data.checked_at);
      toast.success('Diagnostics complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostics failed');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const connectedCount = providers ? Object.values(providers).filter(p => p.status === 'connected').length : 0;
  const totalCount = providers ? Object.keys(providers).length : 0;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-3">
              <Activity className="w-6 h-6 text-primary" />
              Provider Diagnostics
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Verify connectivity to all AI providers</p>
          </div>
          <Button
            onClick={runDiagnostics}
            disabled={loading}
            className="bg-primary hover:bg-primary/90"
            data-testid="run-diagnostics-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Run Diagnostics</>
            )}
          </Button>
        </div>

        {/* Summary Banner */}
        {providers && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${
            connectedCount === totalCount
              ? 'bg-green-500/5 border-green-500/20'
              : 'bg-amber-500/5 border-amber-500/20'
          }`} data-testid="diagnostics-summary">
            <div className="flex items-center gap-3">
              {connectedCount === totalCount ? (
                <Wifi className="w-5 h-5 text-green-400" />
              ) : (
                <WifiOff className="w-5 h-5 text-amber-400" />
              )}
              <div>
                <p className={`text-sm font-semibold ${connectedCount === totalCount ? 'text-green-400' : 'text-amber-400'}`}>
                  {connectedCount}/{totalCount} Providers Connected
                </p>
                {checkedAt && (
                  <p className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    Last check: {new Date(checkedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <Cpu className="w-5 h-5 text-zinc-600" />
          </div>
        )}

        {/* Provider Cards */}
        {!providers && !loading && (
          <div className="text-center py-20">
            <Activity className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 mb-2">No diagnostics run yet</p>
            <p className="text-xs text-zinc-600">Click "Run Diagnostics" to check all provider connections</p>
          </div>
        )}

        {loading && !providers && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-zinc-400">Checking provider connections...</p>
          </div>
        )}

        {providers && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="provider-cards">
            {Object.entries(providers).map(([key, prov]) => {
              const meta = PROVIDER_META[key] || { label: key, capability: '', icon: Cpu, color: 'from-zinc-500/20 to-zinc-600/20', accent: 'zinc' };
              const Icon = meta.icon;
              const statusCfg = STATUS_DISPLAY[prov.status] || STATUS_DISPLAY.unknown;
              const StatusIcon = statusCfg.icon;

              return (
                <div
                  key={key}
                  className={`bg-gradient-to-br ${meta.color} border border-zinc-800 rounded-xl p-5 transition-all hover:border-zinc-700`}
                  data-testid={`provider-card-${key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-zinc-900/60 flex items-center justify-center">
                        <Icon className={`w-5 h-5 text-${meta.accent}-400`} />
                      </div>
                      <div>
                        <p className="font-semibold text-zinc-100">{meta.label}</p>
                        <p className="text-[11px] text-zinc-500">{meta.capability}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${statusCfg.className}`}
                      data-testid={`provider-status-${key}`}>
                      <StatusIcon className="w-3.5 h-3.5" />
                      {statusCfg.label}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Configured</span>
                      <span className={prov.configured ? 'text-green-400' : 'text-red-400'}>
                        {prov.configured ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {prov.default_for?.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Default for</span>
                        <span className="text-zinc-300">{prov.default_for.join(', ')}</span>
                      </div>
                    )}
                    {prov.last_check && (
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Last check</span>
                        <span className="text-zinc-400">{new Date(prov.last_check).toLocaleTimeString()}</span>
                      </div>
                    )}
                    {prov.detail && (
                      <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-400 text-[10px] font-mono break-all">{prov.detail}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
